// adminPackageHelpers.js - Admin-only package management functions
import { 
  collection,
  doc, 
  getDoc,
  getDocs,
  updateDoc, 
  query,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * ACTIVATE PACKAGE - Admin activates package for user
 * @param {Object} userData - { name, email, scansTotal, validityDays, activatedBy }
 * @returns {Promise<Object>} { success: boolean, message: string }
 */
export const activatePackage = async (userData) => {
  try {
    console.log('📦 Activating package for user:', userData.email);
    
    const { name, email, scansTotal, validityDays, activatedBy } = userData;
    
    // Validation
    if (!email || !scansTotal || !validityDays || !activatedBy) {
      return {
        success: false,
        message: 'Ontbrekende vereiste velden'
      };
    }
    
    // Calculate expiry date
    const startDate = new Date();
    const expiryDate = new Date();
    expiryDate.setDate(startDate.getDate() + validityDays);
    
    const userRef = doc(db, 'users', email);
    
    // Check if user exists
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      return {
        success: false,
        message: 'Gebruiker niet gevonden. Gebruiker moet eerst inloggen om account aan te maken.'
      };
    }
    
    // Update user with package info
    await updateDoc(userRef, {
      // Update name if provided
      ...(name && { name }),
      
      // Package fields
      packageScansTotal: scansTotal,
      packageScansUsed: 0,
      packageStartDate: serverTimestamp(),
      packageExpiresAt: expiryDate,
      packageType: 'admin-activated',
      packageActivatedBy: activatedBy,
      packageActivatedAt: serverTimestamp(),
      
      // Metadata
      lastModified: serverTimestamp()
    });
    
    console.log('✅ Package activated successfully:', {
      email,
      scansTotal,
      validityDays,
      expiryDate: expiryDate.toISOString()
    });
    
    return {
      success: true,
      message: `Package geactiveerd voor ${email}. ${scansTotal} scans, geldig tot ${expiryDate.toLocaleDateString('nl-NL')}.`
    };
    
  } catch (error) {
    console.error('❌ Error activating package:', error);
    return {
      success: false,
      message: 'Fout bij activeren package: ' + error.message
    };
  }
};

/**
 * EXTEND PACKAGE - Admin adds more scans/days to existing package
 * @param {string} userEmail - User email
 * @param {number} additionalScans - Additional scans to add
 * @param {number} additionalDays - Additional days to add
 * @returns {Promise<Object>} { success: boolean, newTotals: object, message: string }
 */
export const extendPackage = async (userEmail, additionalScans, additionalDays) => {
  try {
    console.log('🔄 Extending package for:', userEmail, { additionalScans, additionalDays });
    
    const userRef = doc(db, 'users', userEmail);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return {
        success: false,
        newTotals: {},
        message: 'Gebruiker niet gevonden'
      };
    }
    
    const userData = userDoc.data();
    
    // Calculate new totals (additive)
    const currentScansTotal = userData.packageScansTotal || 0;
    const newScansTotal = currentScansTotal + additionalScans;
    
    // Extend expiry date if requested
    let newExpiryDate = userData.packageExpiresAt?.toDate() || new Date();
    if (additionalDays > 0) {
      newExpiryDate = new Date(newExpiryDate);
      newExpiryDate.setDate(newExpiryDate.getDate() + additionalDays);
    }
    
    // Update user document
    await updateDoc(userRef, {
      packageScansTotal: newScansTotal,
      packageExpiresAt: newExpiryDate,
      lastModified: serverTimestamp(),
      
      // Add extension history tracking
      lastExtension: {
        date: serverTimestamp(),
        scansAdded: additionalScans,
        daysAdded: additionalDays,
        previousTotal: currentScansTotal
      }
    });
    
    const newTotals = {
      scansTotal: newScansTotal,
      scansUsed: userData.packageScansUsed || 0,
      scansLeft: newScansTotal - (userData.packageScansUsed || 0),
      expiresAt: newExpiryDate
    };
    
    console.log('✅ Package extended successfully:', newTotals);
    
    return {
      success: true,
      newTotals,
      message: `Package verlengd: +${additionalScans} scans, +${additionalDays} dagen. Nieuwe totaal: ${newScansTotal} scans.`
    };
    
  } catch (error) {
    console.error('❌ Error extending package:', error);
    return {
      success: false,
      newTotals: {},
      message: 'Fout bij verlengen package: ' + error.message
    };
  }
};

/**
 * Helper function: Extract last name from full name
 * @param {string} fullName - Full name (e.g. "Jan van der Berg")
 * @returns {string} Last name for sorting (e.g. "Berg")
 */
const getLastName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(' ');
  return parts[parts.length - 1].toLowerCase();
};

/**
 * GET ALL USERS - Admin overview with filters
 * UPDATED: Haal ALLE users op, sorteer op achternaam, filter op package status
 * @param {Object} filters - { name?, email?, hasActivePackage?: 'active'|'inactive'|'none'|'' }
 * @returns {Promise<Array>} Array of user objects with package status
 */
export const getAllUsers = async (filters = {}) => {
  try {
    console.log('👥 Getting all users with filters:', filters);
    
    // UPDATED: Haal ALLE users op zonder orderBy
    // Dit zorgt ervoor dat ook users zonder lastModified worden opgehaald
    let usersQuery = collection(db, 'users');
    
    // Apply organization filter if provided
    if (filters.organizationId) {
      usersQuery = query(usersQuery, where('organizationId', '==', filters.organizationId));
    }
    
    const snapshot = await getDocs(usersQuery);
    let users = [];
    
    snapshot.forEach(doc => {
      const userData = {
        email: doc.id,
        ...doc.data()
      };
      
      // Calculate package status
      const hasPackage = !!userData.packageScansTotal;
      let isExpired = false;
      let daysLeft = 0;
      
      if (userData.packageExpiresAt) {
        const expiryDate = userData.packageExpiresAt.toDate();
        const now = new Date();
        const timeDiff = expiryDate.getTime() - now.getTime();
        daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
        isExpired = daysLeft <= 0;
      }
      
      const userWithStatus = {
        ...userData,
        hasActivePackage: hasPackage && !isExpired,
        packageStatus: {
          hasPackage,
          isExpired,
          daysLeft: Math.max(0, daysLeft),
          scansUsed: userData.packageScansUsed || 0,
          scansTotal: userData.packageScansTotal || 0,
          scansLeft: (userData.packageScansTotal || 0) - (userData.packageScansUsed || 0)
        }
      };
      
      users.push(userWithStatus);
    });
    
    // UPDATED: Sorteer op achternaam (laatste woord in naam)
    users.sort((a, b) => {
      const lastNameA = getLastName(a.name);
      const lastNameB = getLastName(b.name);
      
      // Als beide geen naam hebben, sorteer op email
      if (!lastNameA && !lastNameB) {
        return a.email.localeCompare(b.email);
      }
      
      // Als één geen naam heeft, die komt onderaan
      if (!lastNameA) return 1;
      if (!lastNameB) return -1;
      
      // Sorteer op achternaam
      return lastNameA.localeCompare(lastNameB);
    });
    
    // Apply client-side name filter
    if (filters.name) {
      const nameFilter = filters.name.toLowerCase();
      users = users.filter(user => 
        user.name?.toLowerCase().includes(nameFilter)
      );
    }
    
    // Apply client-side email filter
    if (filters.email) {
      const emailFilter = filters.email.toLowerCase();
      users = users.filter(user => 
        user.email?.toLowerCase().includes(emailFilter)
      );
    }
    
    // UPDATED: Filter op package status met 4 opties
    if (filters.hasActivePackage !== undefined && filters.hasActivePackage !== '') {
      switch(filters.hasActivePackage) {
        case 'active':
          // Heeft package EN is niet verlopen
          users = users.filter(user => 
            user.packageStatus.hasPackage && !user.packageStatus.isExpired
          );
          break;
        case 'inactive':
          // Heeft package MAAR is verlopen
          users = users.filter(user => 
            user.packageStatus.hasPackage && user.packageStatus.isExpired
          );
          break;
        case 'none':
          // Heeft GEEN package
          users = users.filter(user => 
            !user.packageStatus.hasPackage
          );
          break;
        // case '' or default: show all users (geen filter)
      }
    }
    
    console.log('✅ Retrieved users:', users.length);
    return users;
    
  } catch (error) {
    console.error('❌ Error getting all users:', error);
    return [];
  }
};

/**
 * GET USER PACKAGE HISTORY - Get package activation/extension history
 * @param {string} userEmail - User email
 * @returns {Promise<Array>} Array of package activation/extension history
 */
export const getUserPackageHistory = async (userEmail) => {
  try {
    console.log('📋 Getting package history for:', userEmail);
    
    const userDoc = await getDoc(doc(db, 'users', userEmail));
    
    if (!userDoc.exists()) {
      return [];
    }
    
    const userData = userDoc.data();
    const history = [];
    
    // Add activation record
    if (userData.packageActivatedAt) {
      history.push({
        type: 'activation',
        date: userData.packageActivatedAt.toDate(),
        scansTotal: userData.packageScansTotal,
        activatedBy: userData.packageActivatedBy,
        packageType: userData.packageType
      });
    }
    
    // Add extension record if exists
    if (userData.lastExtension) {
      history.push({
        type: 'extension',
        date: userData.lastExtension.date.toDate(),
        scansAdded: userData.lastExtension.scansAdded,
        daysAdded: userData.lastExtension.daysAdded,
        previousTotal: userData.lastExtension.previousTotal
      });
    }
    
    // Sort by date descending
    history.sort((a, b) => b.date.getTime() - a.date.getTime());
    
    console.log('✅ Package history retrieved:', history.length, 'events');
    return history;
    
  } catch (error) {
    console.error('❌ Error getting package history:', error);
    return [];
  }
};

/**
 * DEACTIVATE PACKAGE - Admin deactivates user package
 * @param {string} userEmail - User email
 * @param {string} reason - Reason for deactivation
 * @returns {Promise<Object>} { success: boolean, message: string }
 */
export const deactivatePackage = async (userEmail, reason) => {
  try {
    console.log('🚫 Deactivating package for:', userEmail, 'Reason:', reason);
    
    const userRef = doc(db, 'users', userEmail);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return {
        success: false,
        message: 'Gebruiker niet gevonden'
      };
    }
    
    const userData = userDoc.data();
    
    if (!userData.packageScansTotal) {
      return {
        success: false,
        message: 'Gebruiker heeft geen actief package'
      };
    }
    
    // Archive current package data and clear active package
    await updateDoc(userRef, {
      // Clear active package fields
      packageScansTotal: null,
      packageScansUsed: null,
      packageStartDate: null,
      packageExpiresAt: null,
      packageType: null,
      packageActivatedBy: null,
      packageActivatedAt: null,
      
      // Archive deactivation info
      packageDeactivatedAt: serverTimestamp(),
      packageDeactivatedReason: reason,
      lastModified: serverTimestamp(),
      
      // Archive previous package for history
      archivedPackage: {
        scansTotal: userData.packageScansTotal,
        scansUsed: userData.packageScansUsed,
        activatedAt: userData.packageActivatedAt,
        activatedBy: userData.packageActivatedBy,
        deactivatedAt: serverTimestamp(),
        deactivatedReason: reason
      }
    });
    
    console.log('✅ Package deactivated successfully');
    
    return {
      success: true,
      message: `Package gedeactiveerd voor ${userEmail}. Reden: ${reason}`
    };
    
  } catch (error) {
    console.error('❌ Error deactivating package:', error);
    return {
      success: false,
      message: 'Fout bij deactiveren package: ' + error.message
    };
  }
};

/**
 * GET PACKAGE STATISTICS - Admin analytics dashboard
 * @returns {Promise<Object>} Package usage statistics
 */
export const getPackageStatistics = async () => {
  try {
    console.log('📊 Calculating package statistics...');
    
    const usersSnapshot = await getDocs(collection(db, 'users'));
    
    const stats = {
      totalUsers: 0,
      usersWithPackages: 0,
      activePackages: 0,
      expiredPackages: 0,
      totalScansGranted: 0,
      totalScansUsed: 0,
      totalScansRemaining: 0,
      packagesExpiringSoon: 0, // within 7 days
      packageTypes: {
        'admin-activated': 0
      }
    };
    
    const now = new Date();
    const weekFromNow = new Date();
    weekFromNow.setDate(now.getDate() + 7);
    
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      stats.totalUsers++;
      
      if (userData.packageScansTotal) {
        stats.usersWithPackages++;
        stats.totalScansGranted += userData.packageScansTotal;
        stats.totalScansUsed += userData.packageScansUsed || 0;
        stats.totalScansRemaining += (userData.packageScansTotal - (userData.packageScansUsed || 0));
        
        // Check if expired
        if (userData.packageExpiresAt) {
          const expiryDate = userData.packageExpiresAt.toDate();
          
          if (expiryDate < now) {
            stats.expiredPackages++;
          } else {
            stats.activePackages++;
            
            // Check if expiring soon
            if (expiryDate <= weekFromNow) {
              stats.packagesExpiringSoon++;
            }
          }
        } else {
          stats.activePackages++;
        }
        
        // Package type tracking
        const packageType = userData.packageType || 'unknown';
        stats.packageTypes[packageType] = (stats.packageTypes[packageType] || 0) + 1;
      }
    });
    
    // Calculate utilization rate
    stats.utilizationRate = stats.totalScansGranted > 0 
      ? ((stats.totalScansUsed / stats.totalScansGranted) * 100).toFixed(1)
      : 0;
    
    console.log('✅ Package statistics calculated:', stats);
    return stats;
    
  } catch (error) {
    console.error('❌ Error calculating package statistics:', error);
    return {
      totalUsers: 0,
      usersWithPackages: 0,
      activePackages: 0,
      expiredPackages: 0,
      totalScansGranted: 0,
      totalScansUsed: 0,
      totalScansRemaining: 0,
      packagesExpiringSoon: 0,
      packageTypes: {},
      utilizationRate: 0
    };
  }
};